import { forwardRef, type ReactNode, type Ref, type ElementType, type HTMLAttributes } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps, resolveAs } from '../_internal/factory.js';
import './Card.css';

/* A Card is a <div>, and callers routinely want it clickable or labelled.
 * Before DOM props were forwarded there was no point declaring these, so
 * pages wrapped cards in a bare <div onClick> instead. */
export interface CardProps extends BaseProps, Omit<HTMLAttributes<HTMLElement>, 'color' | 'title'> {
  as?: ElementType;
  children?: ReactNode;
  /**
   * Optional title rendered with the `headline` typography role.
   *
   * Backwards-compat: the pre-Stage-1 sdk-ui Card had a `title?: string` prop
   * that produced an `<h2 className="rr-card__title">`. Kept identical so
   * tenant-ui Login/Signup/Dashboard imports don't break.
   */
  title?: ReactNode;
  /** Adds 1px border via `surface.border` token. */
  withBorder?: boolean;
  /** Removes default padding (cards holding edge-to-edge media). */
  noPadding?: boolean;
}

interface CardSectionProps extends BaseProps, Omit<HTMLAttributes<HTMLDivElement>, 'color'> {
  children?: ReactNode;
  /** Whether to absorb side padding (so the section can be edge-to-edge). */
  inheritPadding?: boolean;
  withBorder?: boolean;
}

const CardSection = forwardRef(function CardSection(
  { children, inheritPadding, withBorder, ...rest }: CardSectionProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const base = useBaseAttrs('rr-card__section', rest, [
    inheritPadding && 'rr-card__section--inherit-padding',
    withBorder && 'rr-card__section--bordered'
  ]);
  const { dom } = splitBaseProps(rest);
  return (
    <div {...dom} ref={ref} {...base.attrs} className={base.className} style={base.style}>
      {children}
    </div>
  );
});

interface CardComponent
  extends React.ForwardRefExoticComponent<CardProps & React.RefAttributes<HTMLElement>> {
  Section: typeof CardSection;
}

/**
 * Surfaced container with default padding, radius, shadow and optional border.
 * Defaults: `entrance="fadeUp"`, `hover` off (cards don't lift by default).
 */
export const Card = forwardRef(function Card(
  { as, children, title, withBorder, noPadding, entrance, ...rest }: CardProps,
  ref: Ref<HTMLElement>
): ReactNode {
  const Tag = resolveAs(as, 'div');
  const base = useBaseAttrs(
    'rr-card',
    {
      ...rest,
      radius: rest.radius ?? 'lg',
      shadow: rest.shadow ?? 'xs',
      entrance: entrance ?? 'fadeUp'
    },
    [withBorder && 'rr-card--bordered', noPadding && 'rr-card--no-padding']
  );
  const { dom } = splitBaseProps({
      ...rest,
      radius: rest.radius ?? 'lg',
      shadow: rest.shadow ?? 'xs',
      entrance: entrance ?? 'fadeUp'
    });
  return (
    <Tag {...dom} ref={ref} {...base.attrs} className={base.className} style={base.style}>
      {title ? <h2 className="rr-card__title">{title}</h2> : null}
      <div className="rr-card__body">{children}</div>
    </Tag>
  );
}) as CardComponent;

Card.Section = CardSection;
